import React, { useRef, useCallback, useMemo, useState } from 'react';
import styled, { withTheme } from 'styled-components';
import { useStoreState } from 'easy-peasy';
import { scaleLinear } from 'd3-scale';

import { translate } from '~/utils';
import { media } from '~/styles/Utils';

import Grid from './Grid';
import VerticalLine from './VerticalLine';
import Line from './Line';
import Mover from './Mover';
import Tooltip from './Tooltip';
import TooltipText from './TooltipText';
import ChartLabel from './ChartLabel';

const Svg = styled.svg`
  display: block;
`;

const MouseHandler = styled.rect`
  fill: rgba(255, 255, 255, 0);
  stroke: none;
`;

const YAxisLabel = styled(ChartLabel)`
  font-size: 10px;

  ${media.m`
     font-size: 12px;
  `}
`;

const margin = {
  top: 20,
  right: 60,
  bottom: 60,
  left: 5,
};

const PERCENTAGE_COL = config.columns.PERCENTAGE;

const Chart = ({ outerWidth, data, theme }) => {
  const chartRef = useRef();
  const [tooltipPercentile, setTooltipPercentile] = useState(null);

  const isRelView = useStoreState((state) => state.app.isRelView);
  const year = useStoreState((state) => state.app.year);
  const yDomain = useStoreState((state) => state.app.yDomain);
  const xDomain = useStoreState((state) => state.app.xDomain);
  const isZoomView = useStoreState((state) => state.app.isZoomView);

  const numberFormat = useStoreState((state) => state.app.numberFormat);
  const percentile = useStoreState((state) => state.app.percentile);
  const ySelector = useStoreState((state) => state.app.ySelector);
  const isSmallVis = outerWidth < 650;
  const aspectRatio = isSmallVis ? 1 : 0.8;

  const outerHeight = outerWidth * aspectRatio;
  const width = outerWidth - margin.left - margin.right;
  const height = outerHeight - margin.top - margin.bottom;

  const xScale = scaleLinear().domain(xDomain).range([0, width]).clamp(true);
  const yScale = scaleLinear().domain(yDomain).range([height, 0]);

  const currData = useMemo(() => data.find((d) => d.year === year), [
    year,
    data,
  ]);

  const handleMouseMove = useCallback(
    (evt) => {
      const bounds = chartRef.current.getBoundingClientRect();
      const x = evt.clientX - bounds.left;

      const xValue = Math.floor(xScale.invert(x));
      setTooltipPercentile(xValue - 1);
    },
    [chartRef.current, setTooltipPercentile, xScale]
  );

  const handleMouseLeave = useCallback(() => {
    setTooltipPercentile(null);
  }, [setTooltipPercentile]);

  const sortedData = useMemo(() => {
    const dataWithoutYear = data.filter((d) => d.year !== year);

    return [...dataWithoutYear, currData];
  }, [data, currData, year]);

  const clipPathWidth = isZoomView ? width - 2 : width;
  const clipPathX = isZoomView ? 1 : 0;

  return (
    <>
      <Svg ref={chartRef} width={outerWidth} height={outerHeight}>
        <clipPath id="chartClip" clipPathUnits="userSpaceOnUse">
          <rect x={clipPathX} y={0} width={clipPathWidth} height={height} />
        </clipPath>
        <g transform={translate(margin.left, margin.top)}>
          <Grid
            isRelView={false}
            isZoomView={isZoomView}
            width={width}
            height={height}
            xScale={xScale}
            yScale={yScale}
            yAxisLabel="Y-Achsen Beschriftung?"
            xAxisLabel={
              <>
                <tspan>Prozent der Bevölkerung</tspan>
                <tspan x={0} dy={16}>
                  (nach Einkommen gereiht)
                </tspan>
              </>
            }
          />

          {sortedData.map((d) => (
            <Line
              key={d.year}
              data={d.data.map((c) => ({
                x: c[PERCENTAGE_COL],
                y: c[ySelector],
              }))}
              xScale={xScale}
              yScale={yScale}
              color={d.year === year ? theme.colors.highlight : '#ccc'}
            />
          ))}

          <VerticalLine
            xScale={xScale}
            yScale={yScale}
            key={year}
            data={percentile}
            height={height}
            format={numberFormat}
            ySelector={ySelector}
            isRelView={isRelView}
            isZoomView={isZoomView}
            tooltipPercentile={tooltipPercentile}
          />

          {tooltipPercentile !== null && (
            <Tooltip
              data={currData.data[tooltipPercentile]}
              xScale={xScale}
              yScale={yScale}
              format={numberFormat}
              ySelector={ySelector}
            />
          )}

          <MouseHandler
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            x={0}
            y={0}
            height={height}
            width={width}
          />

          {isRelView && (
            <g
              transform={`translate(${width + margin.right - 5}, ${
                height / 2
              })`}
            >
              <YAxisLabel transform="rotate(-90)" textAnchor="middle">
                durchschnittliche Belastung des Bruttoeinkommens in Prozent
              </YAxisLabel>
            </g>
          )}
        </g>
      </Svg>
      {tooltipPercentile !== null && (
        <TooltipText
          data={currData.data[tooltipPercentile]}
          xScale={xScale}
          yScale={yScale}
          format={numberFormat}
          ySelector={ySelector}
          isZoomView={isZoomView}
          isRelView={isRelView}
          year={year}
          percentile={percentile}
        />
      )}
      <Mover
        xScale={xScale}
        margin={margin}
        currData={currData}
        height={height}
        percentile={percentile}
      />
    </>
  );
};

export default withTheme(Chart);
